<div class="title">
  <h3><strong>Terms & Conditions for the Victim Support & Response Services</strong></h3>
  <small>Last updated: 19 June 2023</small>
</div>
<p>
  Cybera offers an end-to-end scam victim support & response solution to individuals who have (or
  suspect they have) been the victim of cyber-related crimes or scams. While this service is
  primarily offered in collaboration with Cybera’s network of partnered financial institutions,
  Cybera may also offer this service to individuals on a fee-free basis (the
  <strong>Services</strong>).
</p>

<p>
  These general terms and conditions (<strong>Terms and Conditions</strong>) govern the use of the
  Services which Cybera Global AG and any of its associated companies, businesses or subsidiaries
  (<strong>Cybera</strong>) may offer to you (You). These Terms and Conditions comprise an agreement
  between Cybera and You.
</p>

<p>
  These Terms and Conditions apply to the exclusion of any terms on any order form or other document
  issued by You. Cybera may vary these Terms and Conditions by giving thirty (30) days written
  notice to You. Such varied Terms and Conditions only apply to users of the Services after the date
  of variation as notified by Cybera.
</p>

<p>
  If You wish to make use of the Services offered by Cybera You must also read these Terms and
  Conditions, together with each of the following Cybera policies, prior to proceeding with the
  Services:
</p>
<ul class="list-inside list-disc">
  <li>
    Cybera Website Terms of Use, available here:
    <a href="https://cybera.io/terms-of-use" target="_blank">https://cybera.io/terms-of-use</a>
  </li>
  <li>
    Cybera Privacy Policy, available here:
    <a href="https://cybera.io/privacy-policy/" target="_blank"
      >https://cybera.io/privacy-policy/</a
    >
  </li>
</ul>

<p>
  If you have any queries or comments in relation to these Terms and Conditions or the Services, you
  can contact Cybera by:
</p>
<ul class="list-inside list-disc">
  <li>
    Emailing our victim support and response team at:
    <a href="mailto:response@cybera.io">response&#64;cybera.io</a>; or
  </li>
  <li>
    Contacting Cybera using our online tool, available here:
    <a href="https://cybera.io/contact-us/" target="_blank">https://cybera.io/contact-us/</a>.
  </li>
</ul>
<h4>1 Definitions and interpretation</h4>
<h5>1.1 In these Terms and Conditions, unless the context otherwise requires:</h5>
<p>
  <strong>Force Majeure Event</strong> means any event or circumstance which is beyond the
  reasonable control of Cybera including strikes and other industrial action, fires, explosions,
  floods, riots, lock-outs, injunctions, interruptions of transportation, accidents, war, acts of
  terrorism, governmental action, viruses, pandemics, epidemics, public health emergencies and
  governmental actions, laws and regulations taken in response to same.
</p>
<p><strong>Insolvency Event</strong> means:</p>
<ul class="no-list-style">
  <li>
    (a) in relation to a body corporate or other entity, a liquidation or winding up, the
    appointment of a controller, administrator, receiver, manager or similar insolvency
    administrator to a party or any substantial part of its assets or the entering into a scheme or
    arrangement with creditors;
  </li>
  <li>
    (b) in relation to an individual, becoming bankrupt or entering into a scheme or arrangement
    with creditors; or
  </li>
  <li>
    (c) in relation to any of the foregoing, the occurrence of any event that has a substantially
    similar effect to any of the above events.
  </li>
</ul>
<p>
  <strong>Parties</strong> means each of the parties to these Terms and Conditions, being Cybera and
  You.
</p>
<p>
  <strong>Report</strong> means a report submitted to the Cybera website detailing a cyber-related
  crime or scam. You can make a report in your own capacity, on behalf of a third party or as a
  witness to a cyber-related crime.
</p>
<p>
  <strong>User</strong> means an individual user of the Cybera platform, including Cybera’s
  websites, the Scam Response Platform or any other service or offering made available by Cybera.
</p>
<p>
  <strong>Scam Response Solution</strong> means the victim support and response solution offered by
  Cybera through its website, which allows Users to submit Reports.
</p>
<h5>1.2 Headings are for convenience only and do not form part of these Terms and Conditions.</h5>
<h5>1.3 References to the singular include the plural and the plural include the singular.</h5>
<h4>Services</h4>
<h5>
  2.1 To be eligible for the Services, You must submit a genuine Report through the Scam Response
  Solution.
</h5>
<h5>
  2.2 Before submitting a Report, You must check all details of the Report and ensure they are
  accurate and complete. To the extent permitted by law, Cybera will not be liable for any loss or
  detriment suffered due to inaccurate or incomplete information being submitted in a Report.
</h5>
<h5>
  2.3 On receipt of your Report, Cybera may choose to provide You the Services in accordance with
  these Terms and Conditions, which may include:
</h5>
<ul class="no-list-style">
  <li>
    (a) providing recommendations and initial Report response, and ongoing support in the management
    of responding to and reporting the cyber-related crime detailed in a Report;
  </li>
  <li>
    (b) reporting or otherwise passing along Your Report (or certain details from Your Report) to
    the appropriate law enforcement agencies or financial institutions; and
  </li>
  <li>(c) offering to provide You with Asset Tracking Services, in accordance with clause 5.2.</li>
</ul>
<h5>2.4 Cybera will notify You if Cybera chooses to provide You the Services:</h5>
<ul class="no-list-style">
  <li>(a) by emailing You using the contact details provided as part of Your Report; and/or</li>
  <li>(b) through Your registered Cybera Account (if applicable).</li>
</ul>
<h5>
  2.5 You acknowledge and agree that whether or not Cybera provides the Services to You, Cybera may
  still:
</h5>
<ul class="no-list-style">
  <li>
    (a) utilise the deidentified (from the User’s perspective) information and details the User has
    provided to Cybera in their Report as part of the Cybera ‘Cybercrime Watchlist’ (for more
    information, please refer to the Terms of Use); and/or
  </li>
  <li>
    (b) provide to the User certain of the Services on an ad hoc basis, in which case the provision
    of these Services is still governed by these Terms and Conditions.
  </li>
</ul>

<h5>
  2.6 While some of Cybera’s Services offer victims a chance to potentially locate and recover funds
  lost to cyber-related crime, You acknowledge that the Cybera Services are:
</h5>
<ul class="no-list-style">
  <li>
    (a) primarily focused on providing support and assistance to victims of cyber-related crime; and
  </li>
  <li>(b) not intended to be used to locate or identify perpetrators of cyber-related crimes.</li>
</ul>

<h4>Your Obligations</h4>
<ul class="no-list-style">
  <li>
    (a) In addition to the requirements under the Cybera Terms of Use, which govern the use of
    Cybera’s websites, You must:
    <ul class="list-inside list-disc">
      <li>
        (i) ensure that all information provided as part of Your Report is true, accurate and not
        misleading, including that You are authorised to provide all information in Your Report;
      </li>
      <li>
        (ii) not use the Scam Response Solution or any of the Services for unlawful, unethical or
        fraudulent purposes, or for any other purpose other than their intended use;
      </li>
      <li>
        (iii) promptly notify Cybera or update Your details (or the details of Your Report) through
        Your Account if You become aware that any information provided through Your Report is, or
        has become, outdated, misleading or untrue;
      </li>
      <li>
        (iv) not use the Services or the Scam Response Solution, or otherwise submit a Report, on
        behalf of another person or entity without that person’s or entity’s express authorisation;
        and
      </li>
      <li>
        (v) not use, provide or advertise the Services or the Scam Response Solution as part of an
        on-sale or fee-for-service arrangement, without the prior written consent of Cybera.
      </li>
    </ul>
  </li>
  <li>
    (b) In order for Cybera to perform the Services, You authorise Cybera (and any agents acting for
    or on behalf of Cybera) to submit your Report, in whole or in part, and any materials related to
    your Report which may include your contact details and identifying information, to law
    enforcement agencies and any relevant third parties (which may include financial institutions,
    government agencies or regulators). You may revoke this authority at any time by providing
    written notice to Cybera, however you acknowledge that without this authority, Cybera may not be
    able to provide you with some or all of the Services.
  </li>
</ul>
<h4>Asset Tracking Services</h4>
<p>
  Cybera may offer to provide You with the Asset Tracking Services for a Fee (as set out in clause
  5.2). Cybera will detail the Asset Tracking Services to be provided at the time of communicating
  its offer and Fees to You. Such Asset Tracking Services may include:
</p>
<ul class="no-list-style">
  <li>(a) delivering an initial finding limited to identifying cash-out VASPs/Exchanges; and</li>
  <li>(b) triggering the initial freezing of stolen funds.</li>
</ul>
<h4>5 Fees</h4>
<ul class="no-list-style">
  <li>
    5.1 Cybera may choose to provide Services without charge or fee (except for the Asset Tracking
    Services, under clause 5.2).
  </li>
</ul>
<ul class="no-list-style">
  <li>
    5.2 Cybera may also offer to provide You the Asset Tracking Services, and any other services on
    an ad hoc basis, in exchange for a fee. In such a case, Cybera will notify You by email, setting
    out:
    <ul class="no-list-style">
      <li>
        (a) the details of the Asset Tracking Services (and/or any other services) proposed to be
        provided by Cybera; and
      </li>
      <li>(b) the fees for each service <strong>(Fees)</strong>.</li>
    </ul>
  </li>
  <li>
    5.3 Unless otherwise stated in writing by Cybera at the time of notifying the Fees to You, all
    Fees are payable upfront, and Cybera is under no obligation to perform any services for Fees
    under these Terms and Conditions unless and until it has received payment in full for those
    Services.
  </li>
</ul>
<h4>6 Australian Consumer Law</h4>
<ul class="no-list-style">
  <li>
    (a) Nothing in these Terms and Conditions is intended to exclude, restrict or modify any rights
    which You may have under Schedule 2 of the Competition and Consumer Act 2010 (Cth)
    (<strong>ACL</strong>). If You are a Consumer, then the Services may be subject to consumer
    guarantees that cannot be excluded under the Australian Consumer Law.
  </li>
  <li>
    (b) If You are a consumer within the meaning of section 3 of the ACL
    (<strong>Consumer</strong>), and the Services are deemed to be ordinarily acquired for personal,
    domestic or household use:
    <ul class="no-list-style">
      <li>
        (i) for major failures with the Services, You are entitled to cancel the Services and to a
        refund of any amounts paid by You for the unused portion of those Services (or compensation
        for their reduced value); and
      </li>
      <li>
        (ii) You are entitled to compensation for any other reasonably foreseeable loss or damage
        from a failure in the Services,
      </li>
      and nothing in these Terms and Conditions should be interpreted as attempting to exclude,
      restrict or modify such guarantees or the rights.
    </ul>
  </li>
  <li>
    (c) If You are a Consumer, and the Services are not deemed to be ordinarily acquired for
    personal, domestic or household use, Cybera’s liability to you in connection with any breach of
    consumer guarantees in respect of the Services provided is limited (at Cybera’s discretion) to:
    <ul class="no-list-style">
      <li>(i) the supplying of the Services again; or</li>
      <li>(ii) the payment of the cost of having the Services supplied again.</li>
    </ul>
  </li>
</ul>
<h4>7 Liability</h4>
<ul class="no-list-style">
  <li>
    (a) Cybera will use its best endeavours to provide the Services to you in an efficient and
    timely manner, however You acknowledge and agree that due to the nature of cyber-related crime,
    it is not always possible to recover funds lost.
  </li>
  <li>
    (b) Subject to clause 6 (which applies to a breach of a consumer guarantee), to the maximum
    extent permitted by law:
    <ul class="no-list-style">
      <li>
        (i) Cybera will not be liable or responsible for any failure to recover any funds lost as a
        result of a cyber-related crime;
      </li>
      <li>
        (ii) Cybera’s total aggregate liability to You arising out of or in connection with, and
        including any claim brought in relation to, these Terms and Conditions or any other act or
        omission by Cybera, will be limited to an amount equal to the total amount actually paid by
        You to us relating to the provision of the Services; and
      </li>
      <li>
        (iii) Cybera will not be liable to You for any loss that does not flow naturally and
        directly from the relevant breach, negligence or other act or omission.
      </li>
    </ul>
  </li>
</ul>
<h4>8 Termination</h4>
<ul class="no-list-style">
  <li>
    (a) Either Party may terminate these Terms and Conditions:
    <ul class="no-list-style">
      <li>
        (i) at any time and for any reason by providing fourteen (14) days written notice to the
        other Party;
      </li>
      <li>
        (ii) immediately, if the other Party has materially breached these Terms and Conditions and
        this breach has not been remedied within seven (7) days of that party being provided notice
        of the material breach; or
      </li>
      <li>
        (iii) subject to applicable laws, immediately if the other Party is the subject of an
        Insolvency Event.
      </li>
    </ul>
  </li>
  <li>
    (b) On termination of these Terms and Conditions:
    <ul class="no-list-style">
      <li>(i) Cybera will immediately cease providing the Services to You;</li>
      <li>
        (ii) Cybera will refund to You any amounts paid as Fees for Services which as at the time of
        termination, have yet to be performed; and
      </li>
      <li>
        (iii) all provisions in these Terms and Conditions which are intended by their nature to
        survive termination, will so survive.
      </li>
    </ul>
  </li>
</ul>
<h4>9 General Terms</h4>
<ul class="no-list-style">
  <li>
    (a) Each Party agrees to receiving notices under these Terms and Conditions using email, or any
    other agreed electronic means.
  </li>
  <li>
    (b) Cybera will not be liable to You for any failure to perform or delay in performing its
    obligations under these Terms and Conditions if that failure or delay is due to a Force Majeure
    Event.
  </li>
  <li>
    (c) You may not assign or otherwise deal with any of its rights, interests or obligations under
    these Terms and Conditions. Cybera may assign or otherwise deal with any of its rights,
    interests or obligations under these Terms and Conditions.
  </li>
  <li>
    (d) No failure to exercise or delay in exercising any right given by or under these Terms and
    Conditions to a Party constitutes a waiver and the Party may still exercise that right in the
    future.
  </li>
  <li>
    (e) Except as expressly stated, nothing in these Terms and Conditions will constitute the
    parties as partners or joint venturers or gives rise to any other form of fiduciary relationship
    between the parties.
  </li>
  <li>
    (f) These Terms are governed by the laws in force in Victoria, Australia, and the parties submit
    to the nonexclusive jurisdiction of the courts of Victoria and the Federal Court of Australia
    and any courts that may hear appeals from those courts about any proceedings in connection with
    these Terms.
  </li>
</ul>
